export class ApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

const explicitOrigin = (import.meta.env.VITE_API_ORIGIN || '').replace(/\/$/, '')
const inferredProductionOrigin = typeof window !== 'undefined' && window.location.hostname === 'app.fairysmiles.co.uk'
  ? 'https://fairysmiles.co.uk'
  : ''
const apiBase = explicitOrigin || (import.meta.env.DEV ? '/static-site' : inferredProductionOrigin)

export function resolveUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path
  }

  if (apiBase === '/static-site') {
    return `${apiBase}${path}`
  }

  if (apiBase !== '') {
    return `${apiBase}${path}`
  }

  return path
}

export function resolveBackendPageUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path
  }

  if (explicitOrigin !== '') {
    return `${explicitOrigin}${path}`
  }

  if (import.meta.env.DEV && typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.hostname}:3001${path}`
  }

  if (inferredProductionOrigin !== '') {
    return `${inferredProductionOrigin}${path}`
  }

  return path
}

async function parseJson<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new ApiError('The server did not return JSON.', response.status)
  }

  return (await response.json()) as T
}

export async function apiGet<T>(url: string): Promise<T> {
  const response = await fetch(resolveUrl(url), {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  })

  const data = await parseJson<T & { message?: string }>(response)
  if (!response.ok) {
    throw new ApiError(data.message || 'Request failed.', response.status)
  }

  return data
}

export async function apiPostForm<T>(url: string, values: Record<string, string>): Promise<T> {
  const formData = new FormData()
  Object.entries(values).forEach(([key, value]) => {
    formData.append(key, value)
  })

  const response = await fetch(resolveUrl(url), {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
    body: formData,
  })

  const data = await parseJson<T & { message?: string }>(response)
  if (!response.ok) {
    throw new ApiError(data.message || 'Request failed.', response.status)
  }

  return data
}

export async function apiPostMultipart<T>(url: string, formData: FormData): Promise<T> {
  const response = await fetch(resolveUrl(url), {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
    body: formData,
  })

  const data = await parseJson<T & { message?: string }>(response)
  if (!response.ok) {
    throw new ApiError(data.message || 'Request failed.', response.status)
  }

  return data
}
